import {
  alpha,
  Box,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AreaPlot,
  axisClasses,
  AxisItemIdentifier,
  ChartDataProvider,
  ChartsAxisHighlight,
  ChartsClipPath,
  ChartsGrid,
  chartsGridClasses,
  ChartsReferenceLine,
  ChartsSurface,
  ChartsTooltipContainer,
  ChartsYAxis,
  lineElementClasses,
  LinePlot,
  MarkElementProps,
  MarkPlot,
  useAxesTooltip,
  useBarSeries,
  useDrawingArea,
  useXAxis,
  useXScale,
  useYScale,
} from "@mui/x-charts";
import { format, sub } from "~/lib/date";
import {
  clampFloat,
  signedFloat,
  useSprintStreakCard,
} from "~/pages/space/home-dashboard/SprintStreakCard/hooks/useSprintStreakCard";
import { useId, useState } from "react";
import { getColorByValue } from "~/pages/space/home-dashboard/SpaceOrWorkspaceRiskCard/components/SpaceRiskGaugeChart/utils/spaceRiskGaugeChartUtils";

export interface SprintStreakChartProps {
  data: ReturnType<typeof useSprintStreakCard>["dailySpaceRisk"];
}

export function SprintStreakCardChart({ data }: SprintStreakChartProps) {
  const theme = useTheme();
  const key = useId();
  const chartId = key + "-sprint-streak";
  const axisGridId = chartId + "-axis-grid";
  const areaMaskId = chartId + "-area-mask";
  const fillGradientId = chartId + "-fill-gradient";
  const clipPathId = chartId + "-clip-path";

  const roundToNext = (increment: number, value: number) =>
    Math.ceil(value / increment) * increment;
  const roundToPrev = (increment: number, value: number) =>
    Math.floor(value / increment) * increment;
  const valueAtPercentage = (min: number, max: number, percentage: number) => {
    return min + (max - min) * (percentage / 100);
  };

  const riskPoints = data.flatMap((d) => {
    const start = {
      risk: d.startRisk,
      time: sub(d.time, { hours: 24 }).toISOString(),
    };
    const end = { risk: d.endRisk, time: d.time };
    return [start, end];
  });

  const riskSeries = data.map((d) => d.endRisk);
  const startRiskSeries = data.map((d) => d.startRisk);
  const riskDeltaSeries = data.map((d) => d.endRisk - d.startRisk);

  const xAxisBand = data.map((d) => new Date(d.time));
  const xAxisTime = riskPoints.map((d) => new Date(d.time));

  const minRisk = Math.min(...riskPoints.map((d) => d.risk));
  const maxRisk = Math.max(...riskPoints.map((d) => d.risk));

  let axisMin = Math.max(roundToPrev(5, minRisk), 0);
  let axisMax = Math.min(roundToNext(5, maxRisk), 100);
  if (axisMin === axisMax) {
    axisMin = Math.max(roundToPrev(5, axisMin - 5), 0);
    axisMax = Math.min(roundToNext(5, axisMax + 5), 100);
  }

  const labeledTickInterval = [0, 50, 100].map((v) =>
    valueAtPercentage(axisMin, axisMax, v),
  );
  const unlabledTickInterval = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(
    (v) => valueAtPercentage(axisMin, axisMax, v),
  );

  const [highlightedAxis, setHighlightedAxis] = useState<AxisItemIdentifier[]>(
    [],
  );

  return (
    <ChartDataProvider
      height={255}
      highlightedAxis={highlightedAxis}
      onHighlightedAxisChange={setHighlightedAxis}
      margin={{
        left: 0,
        right: 76,
        bottom: 0,
      }}
      series={[
        {
          id: "start-risk",
          type: "bar",
          data: startRiskSeries,
          yAxisId: "y-axis-risk",
          valueFormatter: (value) => `${clampFloat(value ?? 0, 2)}`,
        },
        {
          id: "end-risk",
          type: "bar",
          data: riskSeries,
          yAxisId: "y-axis-risk",
          valueFormatter: (value) => `${clampFloat(value ?? 0, 2)}`,
        },
        {
          id: "risk-line",
          type: "line",
          data: riskPoints.map((d) => d.risk),
          yAxisId: "y-axis-risk",
          xAxisId: "x-axis-utc",
          curve: "linear",
          showMark: true,
          area: true,
          valueFormatter: (value) => `${clampFloat(value ?? 0, 2)}`,
        },
        {
          id: "risk-delta",
          type: "bar",
          data: riskDeltaSeries,
          yAxisId: "y-axis-risk",
          valueFormatter: (value) => `${clampFloat(value ?? 0, 2)}`,
        },
      ]}
      xAxis={[
        {
          data: xAxisBand,
          scaleType: "band",
          id: "x-axis",
          valueFormatter: (value: Date, context) => {
            const formatStr =
              context.location === "tooltip" ? "M/dd" : "E M/dd";
            return format(value, formatStr);
          },
          height: 24,
          tickNumber: data.length,
          disableLine: true,
          disableTicks: true,
          barGapRatio: 0,
          categoryGapRatio: 0,
        },
        {
          data: xAxisTime,
          scaleType: "utc",
          id: "x-axis-utc",
          valueFormatter: (value: Date, context) => {
            const sm = true;
            const formatStr =
              sm || context.location === "tooltip" ? "M/dd" : "E M/dd";
            return format(value, formatStr);
          },
          tickNumber: data.length,
          disableLine: true,
          disableTicks: true,
        },
      ]}
      yAxis={[
        {
          id: "y-axis-risk",
          position: "none",
          min: axisMin,
          max: axisMax,
          tickInterval: unlabledTickInterval,
          tickSize: 72,
          width: 102,
          disableLine: true,
          disableTicks: false,
          colorMap: {
            type: "piecewise",
            thresholds: [20, 40, 60, 80, 100],
            colors: [
              theme.palette.none.main,
              theme.palette.low.main,
              theme.palette.medium.main,
              theme.palette.high.main,
              theme.palette.critical.main,
            ],
          },
        },
        {
          id: "y-axis-risk-labeled",
          position: "left",
          min: axisMin,
          max: axisMax,
          tickInterval: labeledTickInterval,
          tickSize: 72,
          width: 102,
          disableLine: true,
          disableTicks: false,
        },
      ]}
    >
      <Box sx={{ position: "relative", pl: 0, pr: 0 }}>
        <ChartsSurface
          sx={{
            [`& .${lineElementClasses.series}-risk-line`]: {
              strokeWidth: 2,
            },
            [`& .${chartsGridClasses.verticalLine}`]: {
              strokeDasharray: "2 2",
              strokeWidth: 1,
            },
            [`& .MuiMarkElement-series-risk-line`]: {
              r: "3.5px",
            },
            [`& .${axisClasses.id}-y-axis-risk-labeled`]: {
              strokeOpacity: 0.12,
            },
          }}
        >
          <ChartsGrid vertical horizontal key={axisGridId} />
          <g clipPath={`url(#${clipPathId})`}>
            <ChartsReferenceLine
              y={80}
              lineStyle={{ stroke: theme.palette.text.secondary }}
            />
            <ChartsReferenceLine
              y={60}
              lineStyle={{ stroke: theme.palette.text.secondary }}
            />
            <ChartsReferenceLine
              y={40}
              lineStyle={{ stroke: theme.palette.text.secondary }}
            />
            <ChartsReferenceLine
              y={20}
              lineStyle={{ stroke: theme.palette.text.secondary }}
            />
            <g mask={`url(#${areaMaskId})`}>
              <AreaPlot />
            </g>
            <LinePlot />
          </g>
          <MarkPlot slots={{ mark: RiskPointMark }} />
          <ChartsClipPath id={clipPathId} />
          <TimeBandAxis highlightedAxis={highlightedAxis} />
          <ChartsYAxis axisId="y-axis-risk" />
          <ChartsYAxis axisId="y-axis-risk-labeled" />
          <ChartsAxisHighlight x="band" />
          <FillGradient id={fillGradientId} color="white" />
          <mask id={areaMaskId} x="0" y="0" width="100%" height="100%">
            <rect x="0" y="0" width="100%" height="100%" fill={`black`} />
          </mask>
        </ChartsSurface>
        <ChartsTooltipContainer>
          <TimeBandTooltip />
        </ChartsTooltipContainer>
        <RangeEndLabels />
      </Box>
    </ChartDataProvider>
  );
}

function RiskPointMark(props: MarkElementProps) {
  const theme = useTheme();
  return (
    <circle
      cx={props.x}
      cy={props.y}
      r={3.5}
      fill={theme.palette.background.default}
      stroke={props.color}
      strokeWidth={2}
    />
  );
}

function RangeEndLabels() {
  const endSeries = useBarSeries("end-risk");
  const startSeries = useBarSeries("start-risk");
  const axis = useXAxis("x-axis");
  const yScale = useYScale();
  if (!endSeries || !startSeries || !axis.data) return undefined;
  const first = startSeries.data[0] ?? 0;
  const last = endSeries.data[endSeries.data.length - 1] ?? 0;
  const firstY = yScale(first ?? 0) ?? 0;
  const lastY = yScale(last ?? 0) ?? 0;
  return (
    <>
      <Paper
        elevation={0}
        sx={{ position: "absolute", top: firstY - 12, left: 32, zIndex: 10 }}
      >
        <Stack
          sx={{
            alignSelf: "flex-start",
            flexDirection: "column",
            alignItems: "center",
            background: (theme) =>
              alpha(theme.palette[getColorByValue(first)].main, 0.1),
            borderRadius: 0.5,
            py: 0.5,
            px: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              lineHeight: "100%",
              color: (theme) =>
                alpha(theme.palette[getColorByValue(first)].main, 1),
            }}
          >
            {clampFloat(first, 2)}
          </Typography>
        </Stack>
      </Paper>
      <Paper
        elevation={0}
        sx={{ position: "absolute", top: lastY - 12, right: 8, zIndex: 10 }}
      >
        <Stack
          sx={{
            alignSelf: "flex-start",
            flexDirection: "column",
            alignItems: "center",
            background: (theme) =>
              alpha(theme.palette[getColorByValue(last)].main, 0.1),
            borderRadius: 0.5,
            py: 0.5,
            px: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              lineHeight: "100%",
              color: (theme) =>
                alpha(theme.palette[getColorByValue(last)].main, 1),
            }}
          >
            {clampFloat(last, 2)}
          </Typography>
        </Stack>
      </Paper>
    </>
  );
}

interface TimeBandAxisProps {
  highlightedAxis: AxisItemIdentifier[];
}

function TimeBandAxis({ highlightedAxis }: TimeBandAxisProps) {
  const theme = useTheme();
  const xAxis = useXAxis("x-axis");
  const xScale = useXScale<"band">("x-axis");
  const drawArea = useDrawingArea();
  const yPos = drawArea.height + drawArea.top + 16;
  const highlightedXAxisIndex = highlightedAxis.find(
    ({ axisId }) => axisId === "x-axis",
  )?.dataIndex;
  const axisData = xAxis.data ?? [];
  return (
    <g>
      {axisData.map((d, i) => {
        // show first, last, and highlighted
        const showLabel =
          i === 0 || i === axisData.length - 1 || i === highlightedXAxisIndex;
        return (
          <g
            key={`rich-axis-item-${i}`}
            transform={`translate(${xAxis.scale(d)}, ${yPos})`}
            opacity={showLabel ? 1 : 0}
          >
            <text
              textAnchor="middle"
              x={xScale.bandwidth() / 2}
              y="8"
              fontSize={10}
              fontWeight="400"
              fill={theme.palette.text.primary}
            >
              {format(d, "E M/dd")}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function TimeBandTooltip() {
  const tooltipData = useAxesTooltip();

  const firstAxisData = tooltipData?.[0];

  const startRiskSeries = firstAxisData?.seriesItems.find(
    (s) => s.seriesId === "start-risk",
  );
  const endRiskSeries = firstAxisData?.seriesItems.find(
    (s) => s.seriesId === "end-risk",
  );
  const riskDeltaSeries = firstAxisData?.seriesItems.find(
    (s) => s.seriesId === "risk-delta",
  );

  if (
    !firstAxisData ||
    !endRiskSeries ||
    !startRiskSeries ||
    !riskDeltaSeries
  ) {
    return null;
  }

  const riskDelta = Number(riskDeltaSeries.value);

  return (
    <Paper
      elevation={2}
      sx={{
        m: 1,
        p: 1,
      }}
    >
      <Grid container spacing={1}>
        <Grid>
          <Typography
            sx={{ fontSize: 14, fontWeight: 500, lineHeight: "16px", mb: 1 }}
          >
            {format(firstAxisData.axisValue, "EEEE, MMMM dd, yyyy")}
          </Typography>
          <Table
            sx={{
              m: 0,
              borderCollapse: "collapse",
              "th, td": {
                px: 1,
                py: 0.5,
                border: "none",
                fontSize: 12,
              },
              td: {
                textAlign: "right",
                fontWeight: 500,
                borderTopRightRadius: 4,
                borderBottomRightRadius: 4,
              },
              th: {
                textAlign: "left",
                borderTopLeftRadius: 4,
                borderBottomLeftRadius: 4,
              },

              ".start-row": {
                "th, td": {
                  background: (theme) =>
                    alpha(theme.palette.text.primary, 0.06),
                },
              },

              ".change-row": {
                "th, td": {
                  py: "10px",
                },
                th: {
                  fontWeight: 500,
                },
                td: {
                  fontWeight: 400,
                },
              },

              ".end-row": {
                "th, td": {
                  background: (theme) =>
                    alpha(
                      theme.palette[
                        getColorByValue(Number(endRiskSeries.value))
                      ].main,
                      0.1,
                    ),
                },
                td: {
                  color: (theme) =>
                    theme.palette[getColorByValue(Number(endRiskSeries.value))]
                      .main,
                },
              },
            }}
          >
            <TableBody>
              <TableRow className="start-row">
                <TableCell component="th">Start of day</TableCell>
                <TableCell>{startRiskSeries.formattedValue}</TableCell>
              </TableRow>
              <TableRow className="change-row">
                <TableCell component="th">Change in space risk</TableCell>
                <TableCell>{signedFloat(riskDelta)}</TableCell>
              </TableRow>
              <TableRow className="end-row">
                <TableCell component="th">End of day</TableCell>
                <TableCell>{endRiskSeries.formattedValue}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </Paper>
  );
}

function FillGradient({ id, color }: { id: string; color?: string }) {
  const { top, height } = useDrawingArea();
  return (
    <defs>
      <linearGradient
        id={id}
        x1={0}
        x2={0}
        y1={top}
        y2={top + height}
        gradientUnits="userSpaceOnUse"
      >
        <stop stopOpacity="0.3" stopColor={color} offset="0%"></stop>
        <stop stopOpacity="0" stopColor={color} offset="100%"></stop>
      </linearGradient>
    </defs>
  );
}
